import { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useSetPhotosetVisibilityMutation } from '../services/assetApi';
import { isInternalAssetTeamUser } from '../assetFeatureFlags';
import { showAlert, STAYS_UNTIL_DISMISSED } from '@/slice/AlertSlice';
import { SEND_TOAST_VARIANTS, type SendTone } from '../share/sendOutcome';
import { isVisibleToClient } from './isVisibleToClient';
import { revealToast, type RevealEmail } from './notifyCopy';
import { VISIBILITY_COPY, type VisibilityTarget } from './visibilityCopy';

/** The two fields of the page's photoset copy this control reads. */
export interface PhotosetVisibilitySubject {
  photosetId: string;
  visibleToClient?: boolean | null;
}

export interface VisibilityDialogState {
  /** The photoset the dialog opened on, which confirm writes. */
  photosetId: string;
  target: VisibilityTarget;
  accountName: string | null;
  /** The PATCH, or the email after it, is in flight. */
  saving: boolean;
  error: string | null;
}

/**
 * DV-3563: the email Make Visible & Notify sends once the reveal has
 * succeeded (the team send refuses a hidden photoset). Resolves, never
 * rejects.
 */
export type NotifyAfterReveal = () => Promise<RevealEmail>;

export interface PhotosetVisibilityControl {
  /** Whether the banner and kebab item may render (spec §2.1). */
  available: boolean;
  visible: boolean;
  openDialog: () => void;
  /** Null while closed. */
  dialog: VisibilityDialogState | null;
  /** `notify` only from a Make Visible dialog with its email on. */
  confirm: (notify?: NotifyAfterReveal) => Promise<void>;
  /** Refused while a save is in flight, so the result has a dialog to land in. */
  cancel: () => void;
}

interface UsePhotosetVisibilityArgs {
  /** The selected photoset, or null (the order-only page, or not loaded). */
  photoset: PhotosetVisibilitySubject | null;
  loading: boolean;
  /** The property's own account — who gains or loses access. */
  accountName: string | null;
  /** Writes the server's answer into the page's photoset copy. */
  onApplied: (photosetId: string, visibleToClient: boolean) => void;
}

interface PendingChange {
  photosetId: string;
  target: VisibilityTarget;
}

/**
 * DV-2861: the client-visibility control's state. One dialog serves both
 * entry points: the banner (while hidden) and the kebab item.
 *
 * The target is captured when the dialog OPENS, together with the photoset:
 * the dialog renders from it and confirm sends it, so a delivery reveal or a
 * photoset switch arriving underneath cannot retitle the dialog or redirect
 * the write. The PATCH is absolute, never a toggle — a stale view's request
 * is a server no-op, not a flip.
 *
 * The role gate mirrors the server's canCrossAccount() (INTERNAL_ADMIN /
 * INTERNAL_POST_PROCESSING, 403 otherwise). It is a UI affordance; the
 * server is the authority.
 *
 * DV-3563: a Make Visible confirmed with its email on sends it once the
 * reveal has answered, holding the dialog until the email has too, and
 * ends on one toast for both. A failed reveal sends nothing.
 */
export function usePhotosetVisibility({
  photoset,
  loading,
  accountName,
  onApplied
}: UsePhotosetVisibilityArgs): PhotosetVisibilityControl {
  const dispatch = useDispatch();
  const [setVisibility, { isLoading: patching }] =
    useSetPhotosetVisibilityMutation();
  const [notifying, setNotifying] = useState(false);
  const saving = patching || notifying;
  const [pending, setPending] = useState<PendingChange | null>(null);
  const [error, setError] = useState<string | null>(null);
  // A second confirm can arrive before `saving` re-renders; this refuses it.
  // Held through the email too, so the dialog cannot close under it.
  const inFlight = useRef(false);
  // Spec §2.6: a result that lands after unmount (browser Back mid-save) is
  // dropped — no write into a page that is gone, no toast on the next one.
  // Except an email that may have gone out unconfirmed (DV-3563): that warns
  // against a resend, wherever the user now is.
  // Set in the effect, not the initializer, so a StrictMode remount re-arms it.
  const mounted = useRef(false);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  const available =
    isInternalAssetTeamUser() && photoset !== null && !loading;
  const visible = isVisibleToClient(photoset?.visibleToClient);
  const photosetId = photoset?.photosetId ?? null;

  const openDialog = useCallback(() => {
    if (!photosetId) return;
    setError(null);
    setPending({ photosetId, target: visible ? 'hidden' : 'visible' });
  }, [photosetId, visible]);

  const cancel = useCallback(() => {
    if (inFlight.current) return;
    setPending(null);
    setError(null);
  }, []);

  const confirm = useCallback(
    async (notify?: NotifyAfterReveal) => {
      if (!pending || inFlight.current) return;
      inFlight.current = true;
      setError(null);
      try {
        const requested = pending.target === 'visible';
        let applied: boolean;
        try {
          const updated = await setVisibility({
            photosetId: pending.photosetId,
            visibleToClient: requested
          }).unwrap();
          // The server's answer wins. A response without the field falls
          // back to what was asked for: undefined would read as visible, and
          // turn a just-hidden set back to Visible on screen.
          applied =
            typeof updated.visibleToClient === 'boolean'
              ? updated.visibleToClient
              : requested;
        } catch {
          // Nothing local changed before the response, so there is nothing
          // to roll back, and no email went. The error stays in the dialog,
          // where the retry happens.
          if (mounted.current) setError(VISIBILITY_COPY.saveFailed);
          return;
        }
        if (!mounted.current) return;
        // The page shows the new state at once, the email still in flight
        // under the dialog: the reveal is done whatever the email comes to.
        onApplied(pending.photosetId, applied);
        let toast: { tone: SendTone; message: string } = {
          tone: 'success',
          message: applied
            ? VISIBILITY_COPY.toastVisible
            : VISIBILITY_COPY.toastHidden
        };
        if (applied && notify) {
          setNotifying(true);
          const email = await notify();
          toast = revealToast(email);
          if (!mounted.current && !email.outcome.unconfirmed) return;
        }
        if (mounted.current) setPending(null);
        dispatch(
          showAlert({
            type: SEND_TOAST_VARIANTS[toast.tone],
            message: toast.message,
            // A shortfall's toast is the only record of who was not emailed
            // once the dialog has closed, and can run to a few dozen words:
            // it stays until closed. A success keeps the default.
            duration: toast.tone === 'success' ? undefined : STAYS_UNTIL_DISMISSED
          })
        );
      } finally {
        inFlight.current = false;
        if (mounted.current) setNotifying(false);
      }
    },
    [pending, setVisibility, onApplied, dispatch]
  );

  return {
    available,
    visible,
    openDialog,
    dialog: pending
      ? {
          photosetId: pending.photosetId,
          target: pending.target,
          accountName,
          saving,
          error
        }
      : null,
    confirm,
    cancel
  };
}
